/** Web wheel tables — compact cell layout (mirrors mobile combined columns). */

import { loadTableColumns, type ColumnState } from "./columnSettings";
import {
  getTableColumnDefaults,
  TABLE_COLUMNS,
  type TableColumnId,
} from "./tableColumnRegistry";

const STORAGE_KEY = "sfe-web-compact-mode-v1";

type CompactListener = (enabled: boolean) => void;
const listeners = new Set<CompactListener>();

export function isWebCompactMode(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setWebCompactMode(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? "1" : "0");
  } catch {
    /* ignore */
  }
  document.body.classList.toggle("sfe-web-compact-mode", enabled);
  for (const listener of listeners) listener(enabled);
}

export function toggleWebCompactMode(): boolean {
  const next = !isWebCompactMode();
  setWebCompactMode(next);
  return next;
}

export function subscribeWebCompactMode(listener: CompactListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function compactSubline(text: string, extraClass = ""): string {
  const classes = new Set(["sfe-web-compact-sub"]);
  for (const part of extraClass.split(/\s+/)) {
    if (part) classes.add(part);
  }
  return `<small class="${[...classes].join(" ")}">${text}</small>`;
}

export function compactStack(primary: string, subline: string, subClass = ""): string {
  return `${primary}${compactSubline(subline, subClass)}`;
}

const COMPACT_TABLE_IDS = new Set(
  Object.keys(TABLE_COLUMNS).filter((id) => id.endsWith("-compact")),
);

export function effectiveWebTableId(tableId: TableColumnId): TableColumnId {
  const compactId = `${tableId}-compact` as TableColumnId;
  if (isWebCompactMode() && COMPACT_TABLE_IDS.has(compactId)) return compactId;
  return tableId;
}

export function resolveWebTableColumns(tableId: TableColumnId): ColumnState[] {
  const effectiveId = effectiveWebTableId(tableId);
  return loadTableColumns(effectiveId, getTableColumnDefaults(effectiveId));
}

export function syncWebCompactBodyClass(): void {
  document.body.classList.toggle("sfe-web-compact-mode", isWebCompactMode());
}
